import type {IModel} from "../../../../../../../data/models/Model";
import type {Field, IFieldAdapter} from "../../../../../../../data/models/field/Field";
import type {AppActions} from "../../../../../../../data/state/AppActions";
import {FieldDataType, Permission, XyiconFeature} from "../../../../../../../generated/api/base";
import CalculatedFieldIcon from "../../../../../../5.0/icons/calculated-field.svg?react";
import DOLFieldIcon from "../../../../../../5.0/icons/link-alt.svg?react";
import CustomValidationFieldIcon from "../../../../../../5.0/icons/custom-validation.svg?react";
import type {AppState} from "../../../../../../../data/state/AppState";
import type {Catalog} from "../../../../../../../data/models/Catalog";

export type FieldIconName = "" | "calculated-field" | "dol-field" | "custom-validation-badge";

export const getTooltipByIcon = (icon: FieldIconName) => {
	switch (icon) {
		case "dol-field":
			return "This field's value is inherited from another object.";
		case "calculated-field":
			return "This field's value is derived using a formula configured in Settings.";
		case "custom-validation-badge":
			return "This field's value will be validated according to the validation rule defined in Settings.";
	}

	return "";
};

export const getIconComponentFromFieldIconName = (icon: FieldIconName) => {
	// TODO: Import, and return the correct V5 icons!
	switch (icon) {
		case "dol-field":
			return DOLFieldIcon;
		case "calculated-field":
			return CalculatedFieldIcon;
		case "custom-validation-badge":
			return CustomValidationFieldIcon;
	}
};

export const getFieldIconAndInheritance = (field: IFieldAdapter, item: IModel, sectionFeature: XyiconFeature, actions: AppActions) => {
	let icon: FieldIconName = "";
	let isXyiconXyiconLink = false;
	let isInheritedFromBoundary = false;

	if (field.hasFormula && field.feature === sectionFeature) {
		icon = "calculated-field";
	} else if (field.hasValidation && field.feature === sectionFeature) {
		icon = "custom-validation-badge";
	} else if (field.displayOnLinks && item.fieldData) {
		//responsible for Module A to Module B (A !== B and B has inherited field) DOL fields icon
		if (!Object.keys(item.fieldData).includes(field.refId)) {
			icon = "dol-field";
			if (field.feature === XyiconFeature.Boundary) {
				isInheritedFromBoundary = true;
			}
		}

		//responsible for module's own DOL field not show the icon
		if (field.feature === item.ownFeature) {
			icon = "";
		}

		//responsible for Xyicon to Xyicon DOL fields correct icon showing
		if (item.ownFeature === XyiconFeature.Xyicon) {
			const links = actions.getLinksXyiconXyicon(item.id);

			if (
				links?.some((link) => {
					const isFieldAssignedToType = actions
						.getFieldsForType(link.object.typeId, field.feature)
						.find((obj) => obj.id === (field as unknown as Field).id)?.value;

					return isFieldAssignedToType && (link.object.fieldData[field.refId] || field.dataType === FieldDataType.Boolean);
				})
			) {
				icon = "dol-field";
				if (field.feature === XyiconFeature.Xyicon) {
					isXyiconXyiconLink = true;
				}
			}
		}

		//responsible for Boundary to Boundary DOL fields correct icon showing
		if (item.ownFeature === XyiconFeature.Boundary) {
			const links = actions.getLinksBoundaryBoundary(item.id);

			if (links?.some((link) => link.object.fieldData[field.refId])) {
				icon = "dol-field";
			}
		}
	}

	return {icon, isXyiconXyiconLink, isInheritedFromBoundary};
};

export const isNameField = (item: IModel, field: IFieldAdapter) => {
	if (field && item) {
		return [XyiconFeature.Portfolio, XyiconFeature.Space].includes(item.ownFeature) && field.refId.includes("name");
	}
};

export const isCatalogModelField = (item: IModel, field: IFieldAdapter) => {
	if (field && item) {
		return item.ownFeature === XyiconFeature.XyiconCatalog && field.refId.includes("model");
	}

	return false;
};

export const isNameOrModelFieldEditable = (item: IModel, field: IFieldAdapter, appState: AppState) => {
	if (field && item) {
		return (isNameField(item, field) || isCatalogModelField(item, field)) && isPermissionEnablesEditing(appState, item);
	}
};

export const isNameOrModelValid = (nameOrModel: string, item: IModel, appState: AppState) => {
	const isCatalog = XyiconFeature.XyiconCatalog === item?.ownFeature;
	const isPortfolio = XyiconFeature.Portfolio === item?.ownFeature;

	return (
		(isCatalog && appState.actions.isModelValidForCatalog(nameOrModel, item as Catalog)) ||
		(isPortfolio && appState.actions.isPortfolioNameValid(nameOrModel, item.id))
	);
};

// "<feature>/type" fields are type fields and they are editable, except the Xyicon type, the Xyicon's type changes with the Model
export const isTypeFieldEditable = (item: IModel, field: IFieldAdapter, appState: AppState) => {
	if (field && item) {
		return (
			((field.refId.includes("type") && ![XyiconFeature.Xyicon].includes(item.ownFeature)) ||
				(field.refId.includes("model") && item.ownFeature === XyiconFeature.Xyicon)) &&
			isPermissionEnablesEditing(appState, item)
		);
	}
};

const isPermissionEnablesEditing = (appState: AppState, item: IModel) => {
	if (item) {
		const {user, actions} = appState;
		let permission = Permission.None;

		if (item.ownFeature === XyiconFeature.Portfolio) {
			permission = actions.getPortfolioPermission(item.id);
		} else if (item.ownFeature === XyiconFeature.XyiconCatalog) {
			permission = user?.isAdmin
				? Permission.Delete
				: (user?.organizationFeaturePermissionList.find((p) => p.feature === XyiconFeature.XyiconCatalog).permission ?? Permission.None);
		} else {
			permission = actions.getModuleTypePermission(item.typeId, item.ownFeature, item.portfolioId);
		}

		return permission > Permission.View;
	}
};
